import { memo, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '@bymason-private/auth-provider';
import { ControllerLeftNavColor, makeStyles, Theme } from '@bymason-private/controller-v1-theme-provider';
import {
  CONTROLLER_LEFT_NAV_EXPANDED_STATE_KEY,
  CONTROLLER_LEFT_NAV_WIDTH,
  CONTROLLER_LEFT_NAV_WIDTH_COLLAPSED,
  Avatar,
  Divider,
  Drawer,
  List,
  UxRocketLaunch,
  ClickAwayListener,
  Typography,
  Tooltip,
} from '@bymason-private/legacy-pattern-components';
import { useGetSubscription } from '@bymason-private/controller-api-hooks';
import {
  ArtifactsIcon,
  ConnectivityIcon,
  DashboardIcon,
  DevicesIcon,
  GroupsIcon,
  HelpIcon,
  LogoutIcon,
  ProjectsIcon,
} from './icons';
import NavLogo from './nav-logo';
import NavListItem from './nav-list-item';
import { ControllerNavAction } from './types';
import AccountMenu from './nav-menus/account-menu';
import ResourcesMenu from './nav-menus/resources-menu';
import ExpandCollapseButton from './expand-collapse-button';
import {
  ControllerVersion,
  useSelectedControllerPref,
  useShowNewControllerTooltip,
} from '../../global-state';

interface ControllerLeftNavProps {
  onNavAction: (action: ControllerNavAction) => void;
}

const useStyles = makeStyles<Theme, { collapse: boolean }>((theme) => ({
  drawer: {
    flexShrink: 0,
    transition: 'all .25s ease-out',
    transitionProperty: 'width',
    width: ({ collapse }) => (collapse ? CONTROLLER_LEFT_NAV_WIDTH_COLLAPSED : CONTROLLER_LEFT_NAV_WIDTH),
    position: 'relative',
  },
  drawerPaper: {
    backgroundColor: ControllerLeftNavColor.DefaultBg,
    maxWidth: ({ collapse }) => (collapse ? CONTROLLER_LEFT_NAV_WIDTH_COLLAPSED : CONTROLLER_LEFT_NAV_WIDTH),
    overflowX: 'hidden',
    transition: 'all .25s ease-out',
    transitionProperty: 'max-width',
    width: '100%',
  },
  listRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    paddingTop: theme.spacing(1.25),
    marginBottom: theme.spacing(0.5),
  },
  topItemsRoot: {
    flex: '1 1 auto',
  },
  bottomItemsRoot: {
    flex: '0 1 auto',
  },
  divider: {
    border: 'none',
    backgroundColor: ControllerLeftNavColor.DividerBg,
    height: 0.5,
    margin: theme.spacing(0.25, 2, 0.35, 2),
  },
  dividerCollapse: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

function ControllerLeftNav({ onNavAction }: ControllerLeftNavProps) {
  const initiallyExpandedState = localStorage.getItem(CONTROLLER_LEFT_NAV_EXPANDED_STATE_KEY) || 'true';
  const { authToken = '', logout = () => null, userInfo } = useAuth();
  const [, updateSelectedControllerVersion] = useSelectedControllerPref();
  const [showNewControllerTooltip, setShowNewControllerTooltip] = useShowNewControllerTooltip();
  const accountNavItemRef = useRef<HTMLDivElement>(null);
  const resourcesNavItemRef = useRef<HTMLDivElement>(null);
  const [getSubscription, subscription, subscriptionError] = useGetSubscription(authToken);
  const [activeNavItem, setActiveNavItem] = useState<ControllerNavAction>(getCurrentNavAction());
  const [openAccountMenu, setOpenAccountMenu] = useState(false);
  const [openResourcesMenu, setOpenResourcesMenu] = useState(false);
  const [collapse, setCollapse] = useState(initiallyExpandedState === 'false');
  const [showFab, setShowFab] = useState(false);
  const classes = useStyles({ collapse });

  const handleExpandCollapseClick = () => {
    localStorage.setItem(CONTROLLER_LEFT_NAV_EXPANDED_STATE_KEY, collapse ? 'true' : 'false');
    setCollapse(!collapse);
  };

  const handleItemClick = (action: ControllerNavAction) => () => {
    processNavAction(action);
  };

  const processNavAction = useCallback(
    (action: ControllerNavAction) => {
      if (action === ControllerNavAction.Logout) {
        window.Sprig('logoutUser');
        logout();
        return;
      }

      if (action === ControllerNavAction.Account || action === ControllerNavAction.Resources) {
        //
        // user clicked on an item that triggers a popup menu
        //
        setActiveNavItem(action);
        action === ControllerNavAction.Account ? setOpenAccountMenu(true) : setOpenResourcesMenu(true);
        return;
      }

      if (action === ControllerNavAction.ControllerV2Link) {
        updateSelectedControllerVersion(ControllerVersion.ControllerV2);
      }

      let nextActiveNavItem: ControllerNavAction | null = action;
      let callCallback = true;

      switch (action) {
        //
        // handle selection of account popup menu-item
        //
        case ControllerNavAction.AccountApiKeys:
        case ControllerNavAction.AccountNotifications:
        case ControllerNavAction.AccountProfile:
        case ControllerNavAction.AccountUsers:
          setOpenAccountMenu(false);
          nextActiveNavItem = ControllerNavAction.Account;
          break;
        //
        // handle selection of resources popup menu-item
        //
        case ControllerNavAction.ResourcesContactSupport:
        case ControllerNavAction.ResourcesDevKits:
        case ControllerNavAction.ResourcesByMason:
        case ControllerNavAction.ResourcesDocs:
        case ControllerNavAction.ResourcesQuickstart:
          setOpenResourcesMenu(false);
          nextActiveNavItem = ControllerNavAction.Resources;
          if (
            action !== ControllerNavAction.ResourcesDevKits &&
            action !== ControllerNavAction.ResourcesContactSupport
          ) {
            callCallback = false;
          }
          break;
      }

      if (nextActiveNavItem) {
        // make sure respective left nav item displays as active
        setActiveNavItem(nextActiveNavItem);
      }

      if (callCallback) {
        // let left-nav consumer handle nav action
        onNavAction(action);
      }
    },
    [logout, onNavAction, updateSelectedControllerVersion]
  );

  const handleMouseOver = useCallback(() => {
    setShowFab(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowFab(false);
  }, []);

  const handleNewControllerTooltipClose = useCallback(() => {
    setShowNewControllerTooltip(false);
  }, [setShowNewControllerTooltip]);

  useEffect(() => {
    getSubscription({});
  }, [getSubscription]);

  useEffect(() => {
    if (subscriptionError) {
      console.error(subscriptionError);
    }
  }, [subscriptionError]);

  return (
    <Drawer
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      variant='permanent'
    >
      <NavLogo collapse={collapse} />
      <List classes={{ root: classes.listRoot }}>
        <div className={classes.topItemsRoot}>
          <NavListItem
            active={activeNavItem === ControllerNavAction.Dashboard}
            collapse={collapse}
            icon={<DashboardIcon />}
            onClick={handleItemClick(ControllerNavAction.Dashboard)}
            text='Dashboard'
          />
          <NavListItem
            active={activeNavItem === ControllerNavAction.Devices}
            collapse={collapse}
            icon={<DevicesIcon />}
            onClick={handleItemClick(ControllerNavAction.Devices)}
            text='Devices'
          />
          <NavListItem
            active={activeNavItem === ControllerNavAction.Groups}
            collapse={collapse}
            icon={<GroupsIcon />}
            onClick={handleItemClick(ControllerNavAction.Groups)}
            text='Groups'
          />
          <NavListItem
            active={activeNavItem === ControllerNavAction.Artifacts}
            collapse={collapse}
            icon={<ArtifactsIcon />}
            onClick={handleItemClick(ControllerNavAction.Artifacts)}
            text='Artifacts'
          />
          <NavListItem
            active={activeNavItem === ControllerNavAction.Projects}
            collapse={collapse}
            icon={<ProjectsIcon />}
            onClick={handleItemClick(ControllerNavAction.Projects)}
            text='Projects'
          />
          {!!subscription && (
            <NavListItem
              active={activeNavItem === ControllerNavAction.Connectivity}
              collapse={collapse}
              icon={<ConnectivityIcon />}
              onClick={handleItemClick(ControllerNavAction.Connectivity)}
              text='Connectivity'
            />
          )}
        </div>
        <div className={classes.bottomItemsRoot}>
          {
            <ClickAwayListener
              mouseEvent='onMouseDown'
              touchEvent='onTouchStart'
              onClickAway={handleNewControllerTooltipClose}
            >
              <Tooltip
                open={showNewControllerTooltip}
                onClose={handleNewControllerTooltipClose}
                disableHoverListener
                arrow
                placement='right'
                PopperProps={{
                  sx: {
                    '& .MuiTooltip-tooltip': {
                      bgcolor: '#453BB4',
                      color: 'white',
                      width: 220,
                      textAlign: 'center',
                      p: 1,
                    },
                    '& .MuiTooltip-arrow': { color: '#453BB4', fontSize: 24 },
                  },
                }}
                title={
                  <Typography fontSize='12px' fontWeight={700} component='div'>
                    You can try the new Controller at any time
                  </Typography>
                }
              >
                <NavListItem
                  active={activeNavItem === ControllerNavAction.ControllerV2Link}
                  colorOverride='#FFB547'
                  collapse={collapse}
                  icon={<UxRocketLaunch sx={{ fontSize: 28, mx: '-6px' }} />}
                  onClick={handleItemClick(ControllerNavAction.ControllerV2Link)}
                  text='Try the new Controller'
                />
              </Tooltip>
            </ClickAwayListener>
          }
          <NavListItem
            active={activeNavItem === ControllerNavAction.Account}
            collapse={collapse}
            dense
            icon={<Avatar src={userInfo?.picture} />}
            iconType='avatar'
            onClick={handleItemClick(ControllerNavAction.Account)}
            ref={accountNavItemRef}
            text='Account &amp; Org.'
          />
          <NavListItem
            active={activeNavItem === ControllerNavAction.Resources}
            collapse={collapse}
            dense
            icon={<HelpIcon />}
            onClick={handleItemClick(ControllerNavAction.Resources)}
            ref={resourcesNavItemRef}
            text='Resources &amp; Help'
          />
          <Divider className={clsx(classes.divider, collapse && classes.dividerCollapse)} />
          <NavListItem
            active={activeNavItem === ControllerNavAction.Logout}
            collapse={collapse}
            dense
            icon={<LogoutIcon />}
            onClick={handleItemClick(ControllerNavAction.Logout)}
            text='Log out'
          />
        </div>
      </List>
      <ExpandCollapseButton
        collapse={collapse}
        navWidth={collapse ? CONTROLLER_LEFT_NAV_WIDTH_COLLAPSED : CONTROLLER_LEFT_NAV_WIDTH}
        onClick={handleExpandCollapseClick}
        show={showFab}
      />
      <div>
        <AccountMenu
          anchorElem={accountNavItemRef.current}
          onClose={() => {
            setOpenAccountMenu(false);
          }}
          onItemClick={processNavAction}
          open={openAccountMenu}
        />
        <ResourcesMenu
          anchorElem={resourcesNavItemRef.current}
          onClose={() => {
            setOpenResourcesMenu(false);
          }}
          onItemClick={processNavAction}
          open={openResourcesMenu}
        />
      </div>
    </Drawer>
  );
}

export default memo(ControllerLeftNav);

export { ControllerNavAction } from './types';

//
//
// helpers
//
//

function getCurrentNavAction() {
  if (location.pathname.includes(ControllerNavAction.Dashboard)) return ControllerNavAction.Dashboard;
  if (location.pathname.includes(ControllerNavAction.Devices)) return ControllerNavAction.Devices;
  if (location.pathname.includes(ControllerNavAction.Groups)) return ControllerNavAction.Groups;
  if (location.pathname.includes(ControllerNavAction.Artifacts)) return ControllerNavAction.Artifacts;
  if (location.pathname.includes(ControllerNavAction.Projects)) return ControllerNavAction.Projects;
  if (location.pathname.includes(ControllerNavAction.Connectivity)) return ControllerNavAction.Connectivity;
  if (location.pathname.includes(ControllerNavAction.Account)) return ControllerNavAction.Account;
  if (location.pathname.includes(ControllerNavAction.ResourcesDevKits)) return ControllerNavAction.Resources;
  return ControllerNavAction.Dashboard;
}
